import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  ShieldCheck, MapPin, CalendarDays, Eye, EyeOff, Trash2, Star,
  LayoutDashboard, ClipboardCheck, MessagesSquare, Menu, X, LogOut,
  Search, Bell, Download, CircleHelp, Building2, Megaphone, Image as ImageIcon,
  ChevronDown, User, Settings, Globe, Wrench,
} from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LangDropdown } from "@/components/Header";
import { AdminSettingsModal } from "@/components/admin/AdminSettingsModal";
import { AdminOverview, useAdminDashboard } from "@/components/admin/AdminOverview";
import { AdminClaims } from "@/components/admin/AdminClaims";
import { AdminMessages } from "@/components/admin/AdminMessages";
import { AdminAds } from "@/components/admin/AdminAds";
import { AdminHero } from "@/components/admin/AdminHero";
import { AdminEvents } from "@/components/admin/AdminEvents";
import { useLanguage } from "@/i18n/LanguageContext";
import { isAdminHost, publicBaseUrl } from "@/lib/host";

const SECTION_TITLES = {
  apercu: ["Tableau de bord", "Vue d'ensemble de la plateforme GoSpot."],
  validation: ["Validation", "Approuve ou refuse les demandes des propriétaires Pro."],
  etablissements: ["Établissements", "Gère la visibilité des spots sur la plateforme."],
  messages: ["Messagerie", "Réponds aux demandes de support des propriétaires Pro."],
  evenements: ["Événements", "Valide et modère les événements soumis sur GoSpot."],
  avis: ["Avis", "Modère les avis des utilisateurs."],
  publicites: ["Publicités", "Gérez vos affiches publicitaires sur GoSpot."],
  banniere: ["Bannière d'accueil", "Changez l'image principale du hero de la page d'accueil."],
};

export default function AdminPage() {
  const { t, lang } = useLanguage();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [active, setActive] = useState("apercu");
  const [drawer, setDrawer] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [maintenance, setMaintenance] = useState(false);
  const profileRef = useRef(null);

  // Bandeau permanent tant que le mode maintenance est actif (pour ne pas l'oublier)
  useEffect(() => {
    api.get("/settings/maintenance").then((r) => setMaintenance(!!r.data?.active)).catch(() => {});
  }, []);

  const disableMaintenance = async () => {
    try {
      await api.put("/admin/maintenance", { active: false });
      setMaintenance(false);
      toast.success(t("Mode maintenance désactivé — le site est de nouveau accessible."));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  // Ferme le menu profil au clic en dehors (indépendant des contextes d'empilement)
  useEffect(() => {
    if (!profileOpen) return;
    const onDown = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [profileOpen]);
  const [spots, setSpots] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [denied, setDenied] = useState(false);
  const [search, setSearch] = useState("");
  const [period, setPeriod] = useState("14");
  const { data: dashboard, reload: reloadDashboard } = useAdminDashboard();

  const loadAll = () => {
    api.get("/admin/stats").catch((e) => {
      if (e.response?.status === 403 || e.response?.status === 401) setDenied(true);
    });
    api.get("/admin/establishments").then((r) => setSpots(r.data)).catch(() => {});
    api.get("/admin/reviews").then((r) => setReviews(r.data)).catch(() => {});
  };

  useEffect(() => {
    if (user === false) navigate(isAdminHost() ? "/" : "/admin/connexion");
    else if (user) {
      if (user.role !== "admin") {
        setDenied(true);
        return;
      }
      loadAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const toggleSpot = async (slug) => {
    try {
      const { data } = await api.patch(`/admin/establishments/${slug}/visibility`);
      toast.success(data.is_hidden ? "Spot masqué" : "Spot visible à nouveau");
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const toggleDemo = async (slug) => {
    try {
      const { data } = await api.patch(`/admin/establishments/${slug}/demo`);
      toast.success(data.is_demo ? t("Statut Démo activé") : t("Statut Démo désactivé"));
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const removeReview = async (id) => {
    if (!window.confirm(t("Supprimer cet avis ?"))) return;
    try {
      await api.delete(`/admin/reviews/${id}`);
      toast.success(t("Avis supprimé"));
      loadAll();
      reloadDashboard();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const exportReport = () => {
    if (!dashboard) return;
    const k = dashboard.kpis;
    const rows = [
      ["Rapport GoSpot Admin", new Date().toLocaleDateString("fr-CA")],
      [],
      ["Indicateur", "Valeur"],
      ["Utilisateurs", k.users],
      ["Établissements", k.establishments],
      ["Événements", k.events],
      ["Réservations", k.reservations],
      ["Avis", k.reviews],
      ["Revenus ($)", k.revenue],
      [],
      ["Date", "Inscriptions", "Réservations", "Revenus ($)"],
      ...dashboard.series.map((s) => [s.date, s.inscriptions, s.reservations, s.revenus]),
    ];
    const csv = rows.map((r) => r.join(";")).join("\n");
    const url = URL.createObjectURL(new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `rapport-gospot-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(t("Rapport exporté en CSV"));
  };

  const submitSearch = (e) => {
    e.preventDefault();
    setActive("etablissements");
  };

  if (!user || denied) {
    return (
      <main className="mx-auto max-w-xl px-4 py-24 text-center" data-testid="admin-denied">
        <ShieldCheck className="mx-auto h-12 w-12 text-slate-300" />
        <h1 className="mt-4 text-2xl font-bold text-slate-900">{t("Accès réservé")}</h1>
        <p className="mt-2 text-sm text-slate-500">{t("Ce tableau de bord est réservé aux administrateurs GoSpot.")}</p>
        <a
          href={isAdminHost() ? publicBaseUrl() : "/"}
          className="mt-6 inline-block rounded-full bg-[#2563EB] px-6 py-3 text-sm font-semibold text-white"
        >
          {t("Retour à l'accueil")}
        </a>
      </main>
    );
  }

  const pendingClaims = dashboard?.kpis?.pending_claims || 0;
  const unreadMessages = dashboard?.kpis?.unread_messages || 0;

  const MENU = [
    { key: "apercu", label: "Tableau de bord", icon: LayoutDashboard },
    { key: "validation", label: "Validation", icon: ClipboardCheck, badge: pendingClaims },
    { key: "etablissements", label: "Établissements", icon: Building2 },
    { key: "messages", label: "Messagerie", icon: MessagesSquare, badge: unreadMessages },
    { key: "evenements", label: "Événements", icon: CalendarDays },
    { key: "avis", label: "Avis", icon: Star },
    { key: "publicites", label: "Publicités", icon: Megaphone },
    { key: "banniere", label: "Bannière d'accueil", icon: ImageIcon },
  ];

  const go = (key) => {
    setActive(key);
    setDrawer(false);
  };

  const q = search.trim().toLowerCase();
  const filteredSpots = q
    ? spots.filter((s) => s.name.toLowerCase().includes(q) || s.category_label?.toLowerCase().includes(q) || s.neighborhood?.toLowerCase().includes(q))
    : spots;

  const sidebar = (
    <div className="flex h-full flex-col bg-[#0C1B33] px-4 py-5 text-slate-300">
      <Link to="/" data-testid="admin-logo" className="flex items-center gap-2 px-2">
        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#2563EB]">
          <MapPin className="h-4.5 w-4.5 text-white" size={18} />
        </span>
        <span>
          <span className="block text-lg font-extrabold leading-none tracking-tight text-white">{t("GoSpot")}</span>
          <span className="mt-0.5 block text-[10px] font-bold uppercase tracking-widest text-blue-300">{t("Admin")}</span>
        </span>
      </Link>

      <p className="mt-6 px-3 text-[10px] font-bold uppercase tracking-widest text-slate-500">{t("Menu principal")}</p>
      <nav className="mt-2 flex-1 space-y-0.5" data-testid="admin-menu">
        {MENU.map((m) => (
          <button
            key={m.key}
            type="button"
            data-testid={`admin-menu-${m.key}`}
            onClick={() => go(m.key)}
            className={`flex w-full items-center justify-between rounded-xl px-3 py-2 text-[13px] transition ${
              active === m.key ? "bg-[#2563EB] font-semibold text-white shadow-[0_4px_14px_rgba(37,99,235,0.4)]" : "hover:bg-white/5 hover:text-white"
            }`}
          >
            <span className="flex items-center gap-2.5">
              <m.icon className={`h-4 w-4 ${active === m.key ? "text-white" : "text-slate-400"}`} />
              {t(m.label)}
            </span>
            {m.badge > 0 && (
              <span
                className={`flex h-4.5 min-w-4.5 items-center justify-center rounded-full px-1 text-[9px] font-bold ${
                  active === m.key ? "bg-white text-[#2563EB]" : "bg-[#2563EB] text-white"
                }`}
                style={{ minWidth: 18, height: 18 }}
                data-testid={`admin-menu-badge-${m.key}`}
              >
                {m.badge}
              </span>
            )}
          </button>
        ))}
      </nav>

      <div className="mt-4 rounded-2xl bg-white/5 p-4" data-testid="admin-help-card">
        <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-[#2563EB]/20">
          <CircleHelp className="h-4 w-4 text-blue-300" />
        </span>
        <h3 className="mt-2.5 text-xs font-bold text-white">{t("Besoin d'aide ?")}</h3>
        <p className="mt-1 text-[11px] leading-relaxed text-slate-400">{t("Consulte la messagerie pour répondre aux propriétaires.")}</p>
        <button
          type="button"
          data-testid="admin-help-button"
          onClick={() => go("messages")}
          className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-full bg-[#2563EB] px-3 py-2 text-[11px] font-semibold text-white transition hover:bg-[#1D4ED8]"
        >
          {t("Messagerie")}
        </button>
      </div>

      <button
        type="button"
        data-testid="admin-logout"
        onClick={async () => {
          // Destination d'abord : même si la maintenance masque l'accueil, l'admin retombe sur la connexion admin
          navigate(isAdminHost() ? "/" : "/admin/connexion");
          await logout();
        }}
        className="mt-3 flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-[13px] font-medium text-slate-400 transition hover:bg-white/5 hover:text-red-400"
      >
        <LogOut className="h-4 w-4" />
        {t("Déconnexion")}
      </button>
      <p className="mt-3 px-3 text-[10px] text-slate-600">{t("© 2026 GoSpot — Tous droits réservés.")}</p>
    </div>
  );

  return (
    <main data-testid="admin-page" className="min-h-screen bg-[#F8FAFC]">
      <Seo title={t("Panneau admin — GoSpot")} siteName="GoSpot" description="Administration de la plateforme GoSpot." />

      {/* SIDEBAR DESKTOP FIXE */}
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-[250px] lg:block" data-testid="admin-sidebar">
        {sidebar}
      </aside>

      {/* DRAWER MOBILE */}
      {drawer && (
        <>
          <div className="fixed inset-0 z-[60] bg-slate-900/40 backdrop-blur-sm lg:hidden" onClick={() => setDrawer(false)} />
          <div className="fixed inset-y-0 left-0 z-[70] w-[270px] overflow-y-auto shadow-2xl lg:hidden" data-testid="admin-drawer">
            <button type="button" onClick={() => setDrawer(false)} aria-label={t("Fermer")} className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-slate-300">
              <X className="h-4 w-4" />
            </button>
            {sidebar}
          </div>
        </>
      )}

      <div className="lg:pl-[250px]">
        {/* HEADER */}
        <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/90 backdrop-blur-md" data-testid="admin-header">
          <div className="flex h-16 items-center gap-3 px-4 sm:px-6 lg:px-8">
            <button
              type="button"
              data-testid="admin-drawer-button"
              onClick={() => setDrawer(true)}
              aria-label={t("Menu")}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-slate-200 text-slate-700 lg:hidden"
            >
              <Menu className="h-5 w-5" />
            </button>

            <form onSubmit={submitSearch} className="min-w-0 flex-1 sm:max-w-md">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  data-testid="admin-global-search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t("Rechercher un établissement, un quartier…")}
                  className="h-10 w-full rounded-full border border-slate-200 bg-slate-50 pl-10 pr-4 text-sm outline-none transition focus:border-[#2563EB] focus:bg-white focus:ring-2 focus:ring-blue-100"
                />
              </div>
            </form>

            <div className="ml-auto flex items-center gap-2">
              <select
                data-testid="admin-period-select"
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                className="hidden h-10 cursor-pointer rounded-full border border-slate-200 bg-white px-3.5 text-xs font-semibold text-slate-700 outline-none transition hover:border-blue-300 md:block"
              >
                <option value="14">{t("14 derniers jours")}</option>
                <option value="7">{t("7 derniers jours")}</option>
              </select>

              <button
                type="button"
                data-testid="admin-header-notifications"
                onClick={() => go("validation")}
                className="relative flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition hover:border-blue-300"
                aria-label={t("Notifications")}
              >
                <Bell className="h-4.5 w-4.5" size={18} />
                {pendingClaims > 0 && (
                  <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold text-white">
                    {pendingClaims}
                  </span>
                )}
              </button>
              <button
                type="button"
                data-testid="admin-header-messages"
                onClick={() => go("messages")}
                className="relative flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition hover:border-blue-300"
                aria-label={t("Messagerie")}
              >
                <MessagesSquare className="h-4.5 w-4.5" size={18} />
                {unreadMessages > 0 && (
                  <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold text-white">
                    {unreadMessages}
                  </span>
                )}
              </button>

              <button
                type="button"
                data-testid="admin-export-report"
                onClick={exportReport}
                className="hidden items-center gap-1.5 rounded-full bg-[#2563EB] px-4 py-2.5 text-xs font-semibold text-white shadow-sm transition hover:bg-[#1D4ED8] sm:flex"
              >
                <Download className="h-3.5 w-3.5" />
                {t("Exporter le rapport")}
              </button>

              {/* PROFIL ADMIN — menu déroulant */}
              <div ref={profileRef} className="relative border-l border-slate-200 pl-3">
                <button
                  type="button"
                  data-testid="admin-profile-menu-button"
                  onClick={() => setProfileOpen((o) => !o)}
                  aria-expanded={profileOpen}
                  aria-haspopup="menu"
                  className="flex items-center gap-2.5 rounded-2xl px-1.5 py-1 transition hover:bg-slate-100"
                >
                  <span className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-[#0C1B33] text-sm font-bold text-white">
                    {user.avatar ? (
                      <img src={`${process.env.REACT_APP_BACKEND_URL}${user.avatar}`} alt={user.name} className="h-full w-full object-cover" />
                    ) : (
                      user.name?.[0]?.toUpperCase() || "A"
                    )}
                  </span>
                  <div className="hidden text-left xl:block">
                    <p className="text-xs font-bold leading-tight text-slate-900">{user.name}</p>
                    <p className="text-[10px] font-medium text-[#2563EB]">{t("Administrateur")}</p>
                  </div>
                  <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${profileOpen ? "rotate-180" : ""}`} />
                </button>
                {profileOpen && (
                  <div data-testid="admin-profile-menu" className="absolute right-0 z-50 mt-2 w-56 rounded-2xl border border-slate-100 bg-white p-2 shadow-xl">
                      <div className="px-3 py-2">
                        <p className="truncate text-sm font-semibold text-slate-900">{user.name}</p>
                        <p className="truncate text-xs text-slate-400">{user.email}</p>
                      </div>
                      <button
                        type="button"
                        data-testid="admin-menu-profile"
                        onClick={() => {
                          setProfileOpen(false);
                          navigate("/profil");
                        }}
                        className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm text-slate-700 transition hover:bg-slate-100"
                      >
                        <User className="h-4 w-4" />
                        {t("Mon profil")}
                      </button>
                      <button
                        type="button"
                        data-testid="admin-menu-settings"
                        onClick={() => {
                          setProfileOpen(false);
                          setSettingsOpen(true);
                        }}
                        className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm text-slate-700 transition hover:bg-slate-100"
                      >
                        <Settings className="h-4 w-4" />
                        {t("Paramètres")}
                      </button>
                      <div className="my-1 border-t border-slate-100" />
                      <p className="flex items-center gap-2 px-3 pb-1 pt-1 text-[11px] font-bold uppercase tracking-wide text-slate-400">
                        <Globe className="h-3.5 w-3.5" />
                        {t("Langue")}
                      </p>
                      <LangDropdown onDone={() => setProfileOpen(false)} />
                      <div className="my-1 border-t border-slate-100" />
                      <button
                        type="button"
                        data-testid="admin-menu-logout"
                        onClick={async () => {
                          setProfileOpen(false);
                          // Destination d'abord : même si la maintenance masque l'accueil, l'admin retombe sur la connexion admin
                          navigate(isAdminHost() ? "/" : "/admin/connexion");
                          await logout();
                        }}
                        className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm text-slate-700 transition hover:bg-slate-100"
                      >
                        <LogOut className="h-4 w-4" />
                        {t("Déconnexion")}
                      </button>
                    </div>
                )}
              </div>
            </div>
          </div>

          {/* BANDEAU MAINTENANCE — permanent tant que le mode est actif */}
          {maintenance && (
            <div data-testid="admin-maintenance-banner" className="flex items-center justify-center gap-3 border-t border-amber-200 bg-amber-50 px-4 py-2">
              <Wrench className="h-3.5 w-3.5 shrink-0 text-amber-600" />
              <p className="truncate text-xs font-semibold text-amber-700">
                {t("Mode maintenance actif — le site public est masqué aux visiteurs.")}
              </p>
              <button
                type="button"
                data-testid="maintenance-banner-disable"
                onClick={disableMaintenance}
                className="shrink-0 rounded-full bg-amber-500 px-3 py-1 text-[11px] font-bold text-white transition hover:bg-amber-600"
              >
                {t("Désactiver")}
              </button>
            </div>
          )}
        </header>

        <AdminSettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} onMaintenanceChange={setMaintenance} />

        {/* CONTENU */}
        <div className="mx-auto max-w-[1600px] px-4 py-5 sm:px-6 lg:px-8">
          {active !== "apercu" && (
            <div className="mb-1">
              <h1 className="text-xl font-extrabold tracking-tight text-slate-900">{t(SECTION_TITLES[active][0])}</h1>
              <p className="mt-0.5 text-xs text-slate-500">{t(SECTION_TITLES[active][1])}</p>
            </div>
          )}

          {active === "apercu" && <AdminOverview data={dashboard} period={period} go={go} />}
          {active === "validation" && <AdminClaims onDecision={reloadDashboard} />}
          {active === "messages" && <AdminMessages onRead={reloadDashboard} />}
          {active === "publicites" && <AdminAds />}
          {active === "banniere" && <AdminHero />}

          {active === "etablissements" && (
            <div className="mt-4 space-y-2" data-testid="admin-spots">
              {filteredSpots.length === 0 && (
                <p className="rounded-2xl border border-dashed border-slate-200 bg-white p-8 text-center text-sm text-slate-400">
                  {lang === "en" ? `No establishment matches “${search}”.` : `Aucun établissement ne correspond à « ${search} ».`}
                </p>
              )}
              {filteredSpots.map((s) => (
                <div key={s.slug} data-testid={`admin-spot-${s.slug}`} className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-3">
                  <img src={s.image} alt={s.name} className="h-11 w-11 shrink-0 rounded-xl object-cover" />
                  <div className="min-w-0 flex-1">
                    <p className="flex items-center gap-2 truncate text-sm font-semibold text-slate-900">
                      {s.name}
                      {s.is_hidden && <span className="rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-bold text-red-500">{t("Masqué")}</span>}
                      {s.is_demo && <span data-testid={`admin-demo-badge-${s.slug}`} className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-600">{t("Démo")}</span>}
                    </p>
                    <p className="text-xs text-slate-400">{s.category_label} • {s.neighborhood}</p>
                  </div>
                  <div className="flex shrink-0 flex-col items-center gap-1">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{t("Démo")}</span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={!!s.is_demo}
                      aria-label={t("Basculer le statut Démo")}
                      data-testid={`admin-demo-toggle-${s.slug}`}
                      onClick={() => toggleDemo(s.slug)}
                      className={`relative h-5 w-9 rounded-full transition ${s.is_demo ? "bg-amber-500" : "bg-slate-300"}`}
                    >
                      <span
                        className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${s.is_demo ? "left-[18px]" : "left-0.5"}`}
                      />
                    </button>
                    <span data-testid={`admin-demo-status-${s.slug}`} className={`text-[10px] font-bold ${s.is_demo ? "text-amber-600" : "text-slate-400"}`}>
                      {s.is_demo ? t("Oui") : t("Non")}
                    </span>
                  </div>
                  <button
                    type="button"
                    data-testid={`admin-toggle-${s.slug}`}
                    onClick={() => toggleSpot(s.slug)}
                    className="flex shrink-0 items-center gap-1.5 rounded-full border border-slate-200 px-3.5 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-blue-300 hover:text-[#2563EB]"
                  >
                    {s.is_hidden ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                    {s.is_hidden ? t("Afficher") : t("Masquer")}
                  </button>
                </div>
              ))}
            </div>
          )}

          {active === "evenements" && <AdminEvents />}

          {active === "avis" && (
            <div className="mt-4 space-y-2" data-testid="admin-reviews">
              {reviews.length === 0 ? (
                <p className="rounded-2xl bg-white p-6 text-center text-sm text-slate-400">{t("Aucun avis pour le moment.")}</p>
              ) : (
                reviews.map((r) => (
                  <div key={r.id} data-testid={`admin-review-${r.id}`} className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#EFF6FF] text-xs font-bold text-[#2563EB]">
                      {r.user_name?.[0]?.toUpperCase() || "A"}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                        {r.user_name}
                        <span className="flex items-center gap-0.5 text-xs font-medium text-amber-500">
                          <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                          {r.rating}/5
                        </span>
                      </p>
                      <p className="truncate text-xs text-slate-500">{r.comment || "—"}</p>
                      <p className="text-[11px] text-slate-400">{t("sur")} {r.item_label}</p>
                    </div>
                    <button
                      type="button"
                      data-testid={`admin-delete-review-${r.id}`}
                      onClick={() => removeReview(r.id)}
                      className="flex shrink-0 items-center gap-1.5 rounded-full border border-slate-200 px-3.5 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-red-300 hover:text-red-500"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      {t("Supprimer")}
                    </button>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
